import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Home, ShieldCheck, Headphones, Tag } from "lucide-react";

const stats = [
  { icon: Home, label: "Homes Connected", value: "500+", animate: false },
  { icon: ShieldCheck, label: "Network Uptime", value: "99.9%" },
  { icon: Headphones, label: "Support", value: "24/7" },
  { icon: Tag, label: "Hidden Charges", value: "None" },
];

function useCountUp(target = 0, duration = 1200) {
  const [val, setVal] = useState(0);
  const started = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || started.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            setVal(Math.floor(p * target));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { ref, val };
}

export default function StatsBar() {
  return (
    <section className="py-10">
      <div className="container-max grid sm:grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => {
          const { ref, val } = useCountUp(typeof s.value === "number" ? s.value : 0);
          const display = typeof s.value === "number" ? val : s.value;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="card p-5 text-center"
              ref={ref}
            >
              <div className="mx-auto mb-2 w-10 h-10 rounded-full bg-brand-orange/10 text-brand-orange flex items-center justify-center">
                <s.icon size={20} />
              </div>
              <div className="text-2xl font-bold">{display}</div>
              <div className="text-sm text-brand-blue/70">{s.label}</div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
